import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { currencyFormatter } from "../../../utils/currencyFormat";
import CustomPagination from "../../../components/customPagination";
import { formatDate } from "../../../utils/formatDate";
import { useGetInvoicesHandler } from "../dataHandlers/getInvoicesHandler";
import DocumentActions from "../../common/DocumentActions";
import DocumentTableStatusChip from "../../common/DocumentTableStatusChip";
import { useNavigate } from "react-router-dom";
import { usePdfActions } from "../../common/DocumentPdf";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import { Chip } from "@mui/material";
import { TRANSACTION_TYPE_MAP } from "../../common/TransactionTypeMapper";
import { DocumentSource } from "../../../constants/appConstants";
import { isDocumentObsolete } from "../../../constants/documentLifecycleStatus";
import { OverallStatus } from "../../../utils/derivePeppolStatus";
import { canDownloadDocumentPdf } from "../../../utils/canDownloadDocumentPdf";
import { getArchiveXmlActionState } from "../../common/archiveXmlActionState";

const pdfActionTooltip = (overallStatus, allowPdf, enabledTitle) => {
  if (allowPdf) {
    return enabledTitle;
  }

  if (overallStatus === OverallStatus.REJECTED) {
    return "Unavailable because document is rejected";
  }

  if (overallStatus === OverallStatus.VALIDATION_FAILED) {
    return "Unavailable because document failed validation";
  }

  return "Unavailable until Peppol processing completes";
};

const getTransactionTypes = (value) => {
  if (!value) return [];

  return value
    .split("")
    .map((char, index) => (char === "1" ? TRANSACTION_TYPE_MAP[index] : null))
    .filter(Boolean);
};

const InvoiceTable = ({
  handlePaginationChange,
  searchQuery = {},
  isPurchase = false,
}) => {
  const navigate = useNavigate();
  const { downloadPdf, handlePrintPdf } = usePdfActions();
  const { invoices, totalDocuments, isLoading } = useGetInvoicesHandler({
    searchQuery,
    isPurchase,
  });

  const columns = [
    {
      headerName: "Peppol Status",
      field: "peppol_status",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <DocumentTableStatusChip
          status={
            params?.row?.meta_info?.peppol_status?.overall_status ?? "PENDING"
          }
        />
      ),
    },
    {
      headerName: "Invoice No",
      field: "document_number",
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="span"
          variant="span"
          onClick={() =>
            navigate(
              isPurchase
                ? `/purchases/invoices/${params.row.id}`
                : `/sales/invoices/${params.row.id}`,
            )
          }
          sx={{
            textDecoration: "none",
            color: "inherit",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
              color: "primary.main",
            },
            "&:focus": {
              outline: "none",
            },
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      headerName: "Invoice Type",
      field: "transaction_type",
      flex: 1,
      renderCell: (params) => {
        const types = getTransactionTypes(params.value);

        return (
          <>
            {types.map((type, index) => (
              <Chip key={index} label={type} size="small" />
            ))}
          </>
        );
      },
    },
    {
      headerName: "Source",
      field: "document_source",
      flex: 1,
      valueGetter: (params) => {
        return params;
      },
    },
    {
      headerName: "Customer Info",
      field: "accounting_customer_party",
      flex: 1,
      valueGetter: (params) => {
        if (!params) return "N/A";
        return params.name;
      },
    },
    {
      headerName: "Invoice Date",
      field: "issue_date",
      flex: 1,
      valueGetter: (params) => {
        return formatDate(params);
      },
    },
    {
      headerName: "Amount",
      field: "payable_amount",
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === "OMR"
          ? `OMR ${currencyFormatter().format(row.payable_amount)}`
          : currencyFormatter(row.document_currency_code).format(
              row.payable_amount,
            ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      renderCell: (params) => {
        const overallStatus =
          params?.row?.meta_info?.peppol_status?.overall_status ??
          OverallStatus.PENDING;
        const documentSource = params?.row?.document_source;
        const allowPdf = canDownloadDocumentPdf(params?.row);
        const allowResubmit =
          documentSource !== DocumentSource.PEPPOL &&
          !isDocumentObsolete(params?.row?.status) &&
          (overallStatus === OverallStatus.REJECTED ||
            overallStatus === OverallStatus.VALIDATION_FAILED);
        const { allowArchiveXml, archiveXmlDisabledReason, showDocumentMls } =
          getArchiveXmlActionState(params?.row);

        return (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: 0.6,
            }}
          >
            <>
              <Tooltip
                title={pdfActionTooltip(
                  overallStatus,
                  allowPdf,
                  "Download Document",
                )}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!allowPdf}
                    onClick={() => {
                      const url = isPurchase
                        ? `purchase-invoices/${params.row.id}/download-pdf`
                        : `sales-invoices/${params.row.id}/download-pdf`;

                      downloadPdf({
                        url,
                        fileName: `INV-${params.row.id}.pdf`,
                      });
                    }}
                    data-testid="download-pdf-button"
                    sx={{
                      "&.Mui-disabled": {
                        opacity: 0.3,
                      },
                    }}
                  >
                    <PictureAsPdfIcon fontSize="small" color="error" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={pdfActionTooltip(
                  overallStatus,
                  allowPdf,
                  "Print Document",
                )}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!allowPdf}
                    onClick={() => {
                      const url = isPurchase
                        ? `purchase-invoices/${params.row.id}/download-pdf`
                        : `sales-invoices/${params.row.id}/download-pdf`;

                      handlePrintPdf({
                        url,
                        fileName: `INV-${params.row.id}.pdf`,
                      });
                    }}
                    data-testid="print-pdf-button"
                    sx={{
                      "&.Mui-disabled": {
                        opacity: 0.3,
                      },
                    }}
                  >
                    <PrintIcon fontSize="small" color="primary" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
            <DocumentActions
              documentId={params?.row?.id}
              documentUuid={params?.row?.uuid}
              documentType={"invoice"}
              documentApiBaseKey={"invoices"}
              isPurchase={isPurchase}
              allowResubmit={allowResubmit}
              allowArchiveXml={allowArchiveXml}
              archiveXmlDisabledReason={archiveXmlDisabledReason}
              showDocumentMls={showDocumentMls}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={invoices}
        columns={columns}
        getRowId={(row) => row.id}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default InvoiceTable;
